/**
 * Tracking de atribución y contactos de NIOXTEC.
 *
 * - Mantiene la política first-touch durante 30 días.
 * - Conserva compatibilidad con la clave local niox_utm_v1.
 * - Rellena los campos ocultos del formulario y mantiene el envío al CRM.
 * - Publica conversiones confirmadas y clics de contacto sin duplicados rápidos.
 */
(() => {
  'use strict';

  if (window.__NIOXTEC_TRACKING_V2__) return;
  window.__NIOXTEC_TRACKING_V2__ = true;

  const STORAGE_KEY = 'niox_utm_v1';
  const TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
  ];
  const CLICK_ID_KEYS = ['fbclid', 'gclid'];
  const ATTRIBUTION_KEYS = UTM_KEYS.concat(CLICK_ID_KEYS, ['landing_page', 'referrer']);
  const PENDING_LEAD_KEY = 'niox_pending_lead_v1';
  const LAST_SUCCESS_KEY = 'niox_last_lead_success_v1';
  const CLICK_DEDUPE_MS = 2000;

  const SCRIPT_EL = document.getElementById('niox-utm-script');
  const CRM_ENDPOINT = SCRIPT_EL ? SCRIPT_EL.getAttribute('data-crm-endpoint') : null;
  const CRM_TOKEN = SCRIPT_EL ? SCRIPT_EL.getAttribute('data-crm-token') : null;
  const recentClicks = new Map();

  const clean = (value, maxLength) => String(value || '').trim().slice(0, maxLength || 255);

  const safeSessionGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };

  const safeSessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_) {
      /* sessionStorage no disponible */
    }
  };

  const safeSessionRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_) {
      /* sessionStorage no disponible */
    }
  };

  const readStored = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data.ts !== 'number' || Date.now() - data.ts > TTL_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      if (!data.landing_page && data.landing) data.landing_page = data.landing;
      return data;
    } catch (_) {
      return null;
    }
  };

  const writeStored = (data) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (_) {
      /* localStorage no disponible */
    }
  };

  const hostMatches = (host, domain) => host === domain || host.endsWith('.' + domain);

  const classifyReferrer = (referrer) => {
    if (!referrer) return { utm_source: 'direct', utm_medium: 'none' };

    try {
      const url = new URL(referrer);
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      const currentHost = window.location.hostname.toLowerCase().replace(/^www\./, '');
      if (host === currentHost) return null;

      if (host === 'gemini.google.com') return { utm_source: 'gemini', utm_medium: 'referral_ai' };
      if (hostMatches(host, 'chatgpt.com') || host === 'chat.openai.com') {
        return { utm_source: 'chatgpt', utm_medium: 'referral_ai' };
      }
      if (hostMatches(host, 'claude.ai')) return { utm_source: 'claude', utm_medium: 'referral_ai' };
      if (hostMatches(host, 'perplexity.ai')) {
        return { utm_source: 'perplexity', utm_medium: 'referral_ai' };
      }
      if (
        hostMatches(host, 'copilot.microsoft.com') ||
        hostMatches(host, 'copilot.cloud.microsoft') ||
        (hostMatches(host, 'bing.com') && url.pathname.toLowerCase().startsWith('/chat'))
      ) {
        return { utm_source: 'copilot', utm_medium: 'referral_ai' };
      }
      if (hostMatches(host, 'instagram.com')) {
        return { utm_source: 'instagram', utm_medium: 'social' };
      }
      if (hostMatches(host, 'messenger.com') || host === 'm.me') {
        return { utm_source: 'messenger', utm_medium: 'social' };
      }
      if (hostMatches(host, 'facebook.com')) {
        return { utm_source: 'facebook', utm_medium: 'social' };
      }
      if (host === 'bing.com' || host.endsWith('.bing.com')) {
        return { utm_source: 'bing', utm_medium: 'organic' };
      }
      if (/^google\.[a-z.]+$/.test(host) || host.endsWith('.google.com')) {
        return { utm_source: 'google', utm_medium: 'organic' };
      }

      return { utm_source: host || 'referral', utm_medium: 'referral' };
    } catch (_) {
      return { utm_source: 'referral', utm_medium: 'referral' };
    }
  };

  const paidAttribution = (params) => {
    if (clean(params.get('gclid'))) return { utm_source: 'google', utm_medium: 'cpc' };
    if (clean(params.get('gbraid'))) return { utm_source: 'google', utm_medium: 'cpc' };
    if (clean(params.get('wbraid'))) return { utm_source: 'google', utm_medium: 'cpc' };
    if (clean(params.get('msclkid'))) return { utm_source: 'bing', utm_medium: 'cpc' };
    if (clean(params.get('fbclid'))) return { utm_source: 'facebook', utm_medium: 'cpc' };
    return null;
  };

  const captureFromContext = () => {
    const stored = readStored();
    if (stored) {
      if (stored.landing && !stored.landing_page) {
        stored.landing_page = stored.landing;
        writeStored(stored);
      }
      return;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const attribution = {
        ts: Date.now(),
        landing_page: clean(window.location.href, 1024),
        referrer: clean(document.referrer, 1024),
      };

      UTM_KEYS.concat(CLICK_ID_KEYS).forEach((key) => {
        const value = clean(params.get(key));
        if (value) attribution[key] = value;
      });

      const fallback = paidAttribution(params) || classifyReferrer(document.referrer);
      if (fallback) {
        if (!attribution.utm_source) attribution.utm_source = fallback.utm_source;
        if (!attribution.utm_medium) attribution.utm_medium = fallback.utm_medium;
      }

      if (!attribution.utm_source && !attribution.utm_medium) return;
      writeStored(attribution);
    } catch (_) {
      /* nunca interrumpir la navegación */
    }
  };

  const getAttribution = () => {
    const stored = readStored() || {};
    const out = {};
    ATTRIBUTION_KEYS.forEach((key) => {
      if (stored[key]) out[key] = stored[key];
    });
    return out;
  };

  const fillHiddenInputs = (root) => {
    try {
      const attribution = getAttribution();
      (root || document)
        .querySelectorAll('input[data-niox-utm], input[data-niox-attribution]')
        .forEach((input) => {
          const key = input.getAttribute('data-niox-attribution') || input.getAttribute('data-niox-utm');
          if (key && attribution[key]) input.value = attribution[key];
        });
    } catch (_) {
      /* silencioso */
    }
  };

  const FIELD_LABELS = {
    tpv_type: 'Producto',
    establishment_type: 'Establecimiento',
    preferred_contact_method: 'Prefiere contacto por',
    service_interest: 'Servicio',
    service_detail: 'Detalle',
    other_service_details: 'Necesita',
  };

  const fieldValue = (form, name) => {
    const el = form.querySelector('[name="contact[' + name + ']"]');
    return el && typeof el.value === 'string' ? el.value.trim() : '';
  };

  const buildMensaje = (form) => {
    const parts = [];
    Object.keys(FIELD_LABELS).forEach((name) => {
      const value = fieldValue(form, name);
      if (value) parts.push(FIELD_LABELS[name] + ': ' + value);
    });
    return parts.join(' · ').slice(0, 2000);
  };

  const sendLeadToCrm = (form) => {
    try {
      if (!CRM_ENDPOINT || !CRM_TOKEN) return;

      const honeypot = form.querySelector('input[name="contact[website]"]');
      if (honeypot && honeypot.value !== '') return;

      const email = fieldValue(form, 'email');
      if (!email) return;

      const payload = Object.assign(
        {
          nombre: fieldValue(form, 'name') || 'Lead web (sin nombre)',
          email: email,
          telefono: fieldValue(form, 'phone'),
          mensaje: buildMensaje(form),
          pagina: clean(window.location.href, 1024),
        },
        getAttribution()
      );

      window
        .fetch(CRM_ENDPOINT, {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Lead-Token': CRM_TOKEN,
          },
          body: JSON.stringify(payload),
        })
        .catch(() => {});
    } catch (_) {
      /* nunca interferir con el envío nativo de Shopify */
    }
  };

  const eventPayload = (extra) => {
    const attribution = getAttribution();
    return Object.assign(
      {
        source: attribution.utm_source || 'direct',
        medium: attribution.utm_medium || 'none',
        campaign: attribution.utm_campaign || '',
        content: attribution.utm_content || '',
        term: attribution.utm_term || '',
        campaign_id: attribution.utm_id || '',
        landing_page: attribution.landing_page || '',
        referrer: attribution.referrer || '',
        fbclid: attribution.fbclid || '',
        gclid: attribution.gclid || '',
      },
      extra || {}
    );
  };

  const ga4EventName = (eventName) => eventName.replace(/[^a-zA-Z0-9_]/g, '_');

  const publishEvent = (eventName, payload, metaMode) => {
    let publishedToShopify = false;

    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
        publishedToShopify = true;
      }
    } catch (_) {
      /* Shopify Analytics no disponible */
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (_) {
      /* dataLayer no disponible */
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', ga4EventName(eventName), payload);
      }
    } catch (_) {
      /* gtag no disponible */
    }

    try {
      if (typeof window.fbq !== 'function') return;

      if (metaMode === 'lead') {
        if (!publishedToShopify) window.fbq('track', 'Lead', payload);
      } else if (metaMode === 'whatsapp') {
        window.fbq('track', 'Lead', payload);
      } else if (metaMode === 'phone') {
        window.fbq('trackCustom', 'NioxtecPhoneClick', payload);
      }
    } catch (_) {
      /* Meta Pixel no disponible */
    }
  };

  const rememberPendingLead = () => {
    safeSessionSet(
      PENDING_LEAD_KEY,
      JSON.stringify({ id: String(Date.now()), path: window.location.pathname })
    );
  };

  const emitConfirmedLead = () => {
    const successMarker = document.querySelector('[data-niox-lead-success]');
    if (!successMarker) return;

    let pending = null;
    let previous = null;
    try {
      pending = JSON.parse(safeSessionGet(PENDING_LEAD_KEY) || 'null');
      previous = JSON.parse(safeSessionGet(LAST_SUCCESS_KEY) || 'null');
    } catch (_) {
      pending = null;
      previous = null;
    }

    if (!pending && previous && previous.path === window.location.pathname) return;

    const payload = eventPayload({
      content_name: 'Formulario de contacto',
      content_category: 'Lead web',
      form_id: successMarker.closest('form') ? successMarker.closest('form').id : '',
    });
    publishEvent('nioxtec:lead_submitted', payload, 'lead');

    safeSessionSet(
      LAST_SUCCESS_KEY,
      JSON.stringify({ path: window.location.pathname, ts: Date.now() })
    );
    safeSessionRemove(PENDING_LEAD_KEY);
  };

  const onSubmit = (event) => {
    try {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!form.closest('[data-contact-form-mode]')) return;
      if (event.defaultPrevented) return;

      fillHiddenInputs(form);
      rememberPendingLead();
      sendLeadToCrm(form);
    } catch (_) {
      /* silencioso */
    }
  };

  const isWhatsappUrl = (url) => {
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'wa.me' || host === 'api.whatsapp.com') return true;
    return host === 'whatsapp.com' && url.pathname.toLowerCase().startsWith('/send');
  };

  const isRapidDuplicate = (key) => {
    const now = Date.now();
    const previous = recentClicks.get(key) || 0;
    recentClicks.set(key, now);
    return now - previous < CLICK_DEDUPE_MS;
  };

  const onContactClick = (event) => {
    try {
      const target = event.target;
      const anchor = target && target.closest ? target.closest('a[href]') : null;
      if (!anchor) return;

      const rawHref = anchor.getAttribute('href') || '';
      if (/^tel:/i.test(rawHref)) {
        if (isRapidDuplicate('phone|' + rawHref)) return;
        publishEvent(
          'nioxtec:phone_click',
          eventPayload({
            content_name: 'Clic en teléfono',
            content_category: 'Contacto',
            link_url: rawHref,
          }),
          'phone'
        );
        return;
      }

      const url = new URL(anchor.href, window.location.href);
      if (!isWhatsappUrl(url)) return;
      if (isRapidDuplicate('whatsapp|' + url.href)) return;
      publishEvent(
        'nioxtec:whatsapp_click',
        eventPayload({
          content_name: 'Clic en WhatsApp',
          content_category: 'Contacto',
          link_url: url.href,
        }),
        'whatsapp'
      );
    } catch (_) {
      /* enlace no reconocible */
    }
  };

  const WA_MARK = 'vengo de la web';
  const WA_DEFAULT_TEXT = 'Hola, vengo de la web de Nioxtec, me gustaría recibir más información';
  const WA_BUSINESS_PHONE_SUFFIX = '613094653';

  const markWhatsappLinks = () => {
    try {
      document
        .querySelectorAll(
          'a[href*="api.whatsapp.com/send"], a[href*="wa.me/"], a[href*="whatsapp.com/send"]'
        )
        .forEach((anchor) => {
          try {
            const url = new URL(anchor.href);
            let phone = '';
            if (url.hostname.replace(/^www\./, '') === 'wa.me') {
              phone = url.pathname.replace(/\D/g, '');
            } else {
              phone = (url.searchParams.get('phone') || '').replace(/\D/g, '');
            }
            if (!phone.endsWith(WA_BUSINESS_PHONE_SUFFIX)) return;
            const text = url.searchParams.get('text') || '';
            if (text.toLowerCase().indexOf(WA_MARK) !== -1) return;
            url.searchParams.set('text', text ? text + ' (vengo de la web)' : WA_DEFAULT_TEXT);
            anchor.href = url.toString();
          } catch (_) {
            /* enlace raro: dejarlo como está */
          }
        });
    } catch (_) {
      /* silencioso */
    }
  };

  const init = () => {
    captureFromContext();
    fillHiddenInputs();
    markWhatsappLinks();
    emitConfirmedLead();
  };

  document.addEventListener('submit', onSubmit, false);
  document.addEventListener('click', onContactClick, false);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('load', () => {
    fillHiddenInputs();
    markWhatsappLinks();
  });
})();
